import { useEffect, useMemo, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useForm } from "../../context/FormContext";
import SearchableDropdown from "../ui/SearchableDropdown";
import { SPAIN_ADDRESS_FLOOR_OPTIONS } from "../../mocks/applicantData";
import {
  getSpainProvinceIdByName,
  SPAIN_PROVINCE_DROPDOWN_OPTIONS,
  SPAIN_PROVINCE_ID_TO_NAME,
} from "../../constants/spainProvinceIds";
import { SPAIN_STREET_TYPE_OPTIONS } from "../../constants/spainStreetTypes";
import {
  isValidSpainPostalFormat,
} from "../../utils/addressFieldValidation";

interface InlineInputProps {
  fieldId?: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  helper?: string;
  inputMode?: "text" | "numeric";
  maxLength?: number;
  className?: string;
}

function InlineInput({
  fieldId,
  label,
  value,
  onChange,
  placeholder,
  helper,
  inputMode,
  maxLength,
  className = "",
}: InlineInputProps) {
  const ok = value.length > 0;
  return (
    <div className={className} id={fieldId}>
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-[13px] font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
      </div>
      <div className="relative">
        <input
          type="text"
          inputMode={inputMode}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          style={{ fontSize: "16px" }}
          className={`w-full h-12 px-4 pr-10 bg-white border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition-all ${
            ok ? "border-emerald-400" : "border-slate-200 focus:border-transparent"
          }`}
        />
        {ok && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-emerald-500" />
          </div>
        )}
      </div>
      {helper && <p className="text-[13px] text-slate-500 mt-1">{helper}</p>}
    </div>
  );
}

type IneLocalidadItem = {
  province_id: string;
  municipio_id: string;
  municipio_name: string;
  localidad_names: string[];
};

function normalizeKey(v: string): string {
  return String(v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

export default function AddressSection() {
  const { t } = useLang();
  const { data, update } = useForm();
  const isRep = data.registrantType === "representative";
  const [ineRows, setIneRows] = useState<IneLocalidadItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch("/ine_localidades_by_name.json")
      .then((r) => (r.ok ? r.json() : {}))
      .then((json) => {
        if (cancelled || !json || typeof json !== "object") return;
        const rows = Object.values(json as Record<string, unknown>)
          .filter((v): v is IneLocalidadItem => !!v && typeof v === "object")
          .map((v) => v as IneLocalidadItem);
        setIneRows(rows);
      })
      .catch(() => {
        if (!cancelled) setIneRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const streetTypeOptions = useMemo(
    () => SPAIN_STREET_TYPE_OPTIONS.map((option) => option.name),
    []
  );

  const municipalityOptions = useMemo(() => {
    if (!data.province) return [];
    const provinceId = getSpainProvinceIdByName(data.province);
    if (!provinceId) return [];
    const names = new Set<string>();
    ineRows.forEach((row) => {
      if (String(parseInt(String(row.province_id || "0"), 10)) === provinceId && row.municipio_name) {
        names.add(row.municipio_name);
      }
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [data.province, ineRows]);

  const localityOptions = useMemo(() => {
    if (!data.province || !data.municipality) return [];
    const provinceId = getSpainProvinceIdByName(data.province);
    if (!provinceId) return [];
    const localities = new Set<string>();
    ineRows.forEach((row) => {
      if (
        String(parseInt(String(row.province_id || "0"), 10)) === provinceId &&
        normalizeKey(row.municipio_name) === normalizeKey(data.municipality)
      ) {
        (row.localidad_names || []).forEach((name) => {
          if (name) localities.add(name);
        });
      }
    });
    return [...localities].sort((a, b) => a.localeCompare(b));
  }, [data.province, data.municipality, ineRows]);

  const postalFormatError =
    data.postalCode.trim().length > 0 && !isValidSpainPostalFormat(data.postalCode)
      ? t("validation.invalidPostal")
      : null;
  const postalError = postalFormatError;

  return (
    <div className="max-w-[480px] mx-auto px-4 pt-6 pb-2">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-md bg-emerald-600 shrink-0" />
          <h1 className="text-xl font-display text-slate-900 leading-tight">
            {isRep ? t("section4.voiceTitle_rep") : t("section4.voiceTitle")}
          </h1>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 overflow-visible">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <SearchableDropdown
              fieldId="field-streetType"
              label={t("section4.streetType")}
              options={streetTypeOptions}
              values={streetTypeOptions}
              value={data.streetType}
              onChange={(v) => update("streetType", v)}
              placeholder={t("section4.streetTypePlaceholder")}
            />

            <InlineInput
              fieldId="field-street"
              label={t("section4.street")}
              value={data.street}
              onChange={(v) => update("street", v.slice(0, 100))}
              placeholder={t("section4.streetPlaceholder")}
              className="w-full"
              maxLength={100}
            />

            <div className="relative z-30 flex gap-2 items-start">
              <InlineInput
                fieldId="field-streetNumber"
                label={t("section4.streetNumber")}
                value={data.streetNumber}
                onChange={(v) => update("streetNumber", v.slice(0, 10))}
                placeholder={t("section4.streetNumberPlaceholder")}
                className="w-24 shrink-0"
                maxLength={10}
              />
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <SearchableDropdown
                    fieldId="field-floorPiso"
                    label={t("section4.floorPiso")}
                    options={SPAIN_ADDRESS_FLOOR_OPTIONS.map((o) => o.label)}
                    values={SPAIN_ADDRESS_FLOOR_OPTIONS.map((o) => o.value)}
                    value={data.floorPiso}
                    onChange={(v) => update("floorPiso", v)}
                    placeholder={t("section4.floorPisoPlaceholder")}
                  />
                  {data.floorPiso ? (
                    <button
                      type="button"
                      aria-label="Clear floor"
                      onClick={() => update("floorPiso", "")}
                      className="absolute right-16 top-[42px] z-[70] -translate-y-1/2 rounded-full bg-white text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-circle-line text-base" />
                    </button>
                  ) : null}
                </div>
              </div>
              <InlineInput
                fieldId="field-floorDoor"
                label={t("section4.floorDoor")}
                value={data.floorDoor}
                onChange={(v) => update("floorDoor", v.slice(0, 10))}
                placeholder={t("section4.floorDoorPlaceholder")}
                className="w-24 shrink-0"
                maxLength={10}
              />
            </div>
          </div>

          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <SearchableDropdown
                fieldId="field-province"
                label={t("section4.province")}
                options={SPAIN_PROVINCE_DROPDOWN_OPTIONS.map((o) => o.name)}
                values={SPAIN_PROVINCE_DROPDOWN_OPTIONS.map((o) => o.name)}
                value={data.province}
                onChange={(v) => {
                  update("province", v);
                  update("municipality", "");
                  update("locality", "");
                  update("city", "");
                }}
                placeholder={t("section4.provincePlaceholder")}
              />
            </div>
            <div className="w-32 shrink-0" id="field-postalCode">
              <label className="block text-[13px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                {t("section4.postalCode")}
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={data.postalCode}
                onChange={(e) => update("postalCode", e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder={t("section4.postalCodePlaceholder")}
                maxLength={5}
                style={{ fontSize: "16px" }}
                className={`w-full h-12 px-4 bg-white border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition-all ${
                  postalError
                    ? "border-rose-300"
                    : data.postalCode.length === 5 && isValidSpainPostalFormat(data.postalCode)
                      ? "border-emerald-400"
                      : "border-slate-200 focus:border-transparent"
                }`}
              />
              {postalError ? <p className="text-[13px] text-rose-500 mt-1">{postalError}</p> : null}
            </div>
          </div>

          <SearchableDropdown
            fieldId="field-municipality"
            label={t("section4.municipality")}
            options={municipalityOptions}
            values={municipalityOptions}
            value={data.municipality}
            onChange={(v) => {
              update("municipality", v);
              update("locality", "");
              update("city", "");
            }}
            placeholder={t("section4.municipalityPlaceholder")}
          />

          <SearchableDropdown
            fieldId="field-locality"
            label={t("section4.locality")}
            options={localityOptions}
            values={localityOptions}
            value={data.locality}
            onChange={(v) => {
              update("locality", v);
              update("city", v);
            }}
            placeholder={t("section4.localityPlaceholder")}
          />
        </div>
      </div>
    </div>
  );
}
